import styled from 'styled-components';
import { windowMaxWidth } from '../breakpoints';

const NAVIGATION_HEIGHT = 60;
const HEADER_HEIGHT = 101; // 100px + 1px border
const WARNING_HEIGHT = 40;
const WARNING_HEIGHT_MOBILE = 52;
const RESERVED_HEIGHT = NAVIGATION_HEIGHT + HEADER_HEIGHT;
const RESERVED_HEIGHT_WITH_WARNING = RESERVED_HEIGHT + WARNING_HEIGHT;

const RESERVED_HEIGHT_MOBILE = HEADER_HEIGHT;
const RESERVED_HEIGHT_WITH_WARNING_MOBILE = RESERVED_HEIGHT_MOBILE + WARNING_HEIGHT_MOBILE;

const RESERVED_HEIGHT_STANDALONE = HEADER_HEIGHT;
const RESERVED_HEIGHT_WITH_WARNING_STANDALONE = HEADER_HEIGHT + WARNING_HEIGHT_MOBILE;

export const MAX_LINES = 9;
export const LINE_HEIGHT_MESSAGE_USER = 20;
export const LINE_HEIGHT_MESSAGE_HISTORY = 16;

// Styles for the WorklistMessagesList component
const WorklistMessagesWrapper = styled.div`
  background-color: var(--secondary-whisper);
  display: flex;
  flex-direction: column;
`;

const WorklistMessagesListWrapper = styled.div`
  background-color: var(--secondary-whisper);
  display: flex;
  flex-direction: column;
  height: calc(100dvh - ${(props) => (props.$hasWarning ? RESERVED_HEIGHT_WITH_WARNING : RESERVED_HEIGHT)}px);
  overflow: auto;
  scrollbar-color: rgb(136 136 136 / 30%) transparent;
  scrollbar-width: thin;

  @media (${windowMaxWidth.mobile}) {
    height: calc(
      100dvh - ${(props) => (props.$hasWarning ? RESERVED_HEIGHT_WITH_WARNING_MOBILE : RESERVED_HEIGHT_MOBILE)}px
    );
  }

  @media (display-mode: standalone) {
    height: calc(
      100dvh -
        ${(props) => (props.$hasWarning ? RESERVED_HEIGHT_WITH_WARNING_STANDALONE : RESERVED_HEIGHT_STANDALONE)}px
    );
  }
`;

// Styles for the WorklistMessage component

const WorklistMessageWrapper = styled.div`
  background-color: var(--secondary-whisper);
  border-bottom: 1px solid var(--greyscale-dark-white);
  display: flex;
  flex-flow: row wrap;
  padding: 20px 40px 20px 30px;

  &.message {
    padding: 15px 20px 15px 0;
  }

  &.message-from-own-org {
    padding: 15px 0 15px 20px;
  }

  &.unread {
    background-color: var(--greyscale-white);

    &.last-unread {
      border-bottom: 2px solid var(--primary-oqio-blue);
      padding-bottom: 30px;
    }
  }

  &.worklist-messages-list-wrapper {
    border-bottom: 1px solid #e9eaed;
    padding-top: 20px;
  }

  &:last-child {
    margin-bottom: 80px;
  }
`;

const WorklistMessageDividerSentinel = styled.div`
  height: 1px;
  scroll-margin-bottom: 100px;
  width: 100%;
`;

const WorklistMessageDividerWrapper = styled.div`
  align-items: center;
  bottom: 31px;
  display: flex;
  height: 30px;
  justify-content: center;
  margin-top: -15px;
  position: sticky;
  z-index: 99;

  &:last-child {
    margin-bottom: 80px;
  }

  @media (${windowMaxWidth.mobile}) {
    bottom: 33px;
  }
`;

const WorklistUserMessageWrapper = styled.div`
  display: flex;
  flex: 1 0 100%;
  width: 100%;

  &.message-from-own-org {
    flex-direction: row-reverse;
  }
`;

const WorklistMessageContent = styled.div`
  color: var(--greyscale-blackish);
  flex: auto;
  max-width: 100%;

  &.message {
    border: 1px solid var(--greyscale-light-grey);
    border-radius: var(--border-radius-regular);
    padding: 10px 20px;

    @media (${windowMaxWidth.mobile}) {
      max-width: 350px;
    }

    &.unread {
      border: 1px solid var(--greyscale-dark-white);
    }
  }
`;

const WorklistMessageHeader = styled.div`
  align-items: flex-start;
  display: flex;
  flex-wrap: nowrap;
  font-size: var(--font-size-small);
  gap: 10px;
  justify-content: space-between;
  line-height: var(--font-line-height-small);
`;

const WorklistMessageSubHeader = styled.div`
  display: flex;
  flex-flow: column wrap;
  font-size: var(--font-size-small);
  justify-content: space-between;
  line-height: var(--font-line-height-small);
  margin-bottom: 10px;
`;

const WorklistMessageTitle = styled.div`
  color: var(--greyscale-blackish);
  flex: 1 1 auto;
  font-size: var(--font-size-medium);
  font-weight: var(--font-weight-semi-bold);
  letter-spacing: var(--font-letter-spacing-regular);
  line-height: var(--font-line-height-regular);
  margin: 0;
  max-width: 600px;
  min-width: 0;
  overflow-wrap: break-word;
  white-space: normal;
`;

const WorklistMessageRecipients = styled.div`
  color: var(--greyscale-shade);
  display: flex;
  flex-flow: row wrap;
  font-size: var(--font-size-small);
  justify-content: space-between;
  line-height: var(--font-line-height-small);
  overflow-wrap: break-word;
`;

const WorklistMessageCreatorWrapper = styled.div`
  color: var(--greyscale-shade);
  column-gap: 5px;
  display: flex;
  flex-flow: row wrap;
  font-size: var(--font-size-small);
`;

const WorklistMessageCreatorUser = styled.div`
  display: flex;
  line-height: var(--font-line-height-small);
  word-break: normal;
`;

const WorklistMessageCreatorSystem = styled.div`
  color: var(--greyscale-shade);
  display: flex;
  word-break: normal;
`;

const WorklistMessageHeaderTitle = styled.div`
  color: var(--greyscale-blackish);
  flex: 1 1 auto;
  font-size: var(--font-size-medium);
  font-weight: var(--font-weight-semi-bold);
  letter-spacing: var(--font-letter-spacing-regular);
  line-height: var(--font-line-height-regular);
  margin: 0;
  max-width: 328px;
  min-width: 0;
  overflow: hidden;
  overflow-wrap: break-word;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const WorklistMessageHeaderDate = styled.div`
  align-items: flex-end;
  display: flex;
  flex: 1 0 150px;
  flex-direction: column;
  line-height: var(--font-line-height-small);
  padding-top: 2px;
`;

const WorklistMessageDescription = styled.p`
  font-size: var(--font-size-medium);
  line-height: var(--font-line-height-regular);
  margin: 0;
  max-width: 100%;
  overflow: hidden;
  overflow-wrap: anywhere;
  position: relative;
  transition: max-height 0.2s;
  white-space: pre-wrap;
  word-break: normal;

  &.collapsed {
    /* stylelint-disable-next-line property-no-deprecated */
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${MAX_LINES};
    display: -webkit-box;
    max-height: ${MAX_LINES * LINE_HEIGHT_MESSAGE_USER}px;
    text-overflow: ellipsis;
    white-space: pre-line;
  }

  &.expanded {
    /* stylelint-disable-next-line property-no-deprecated */
    -webkit-box-orient: unset;
    -webkit-line-clamp: unset;
    display: block;
    max-height: none;
    overflow: visible;
  }

  @media (${windowMaxWidth.mobile}) {
    font-size: var(--font-size-small);
    line-height: var(--font-line-height-small);

    &.collapsed {
      max-height: 96px;
    }
  }
`;

const WorklistMessageSystemDescription = styled.p`
  font-size: var(--font-size-medium);
  line-height: var(--font-line-height-regular);
  margin: 0;

  @media (${windowMaxWidth.mobile}) {
    font-size: var(--font-size-small);
    line-height: var(--font-line-height-small);
  }
`;

const WorklistMessageDescriptionList = styled.ul`
  display: flex;
  flex-direction: column;
  font-size: var(--font-size-small);
  line-height: var(--font-line-height-small);
  margin: 0;
  padding-left: 15px;
`;

const WorklistMessageDescriptionListItem = styled.li`
  list-style: initial;
  overflow-wrap: break-word;
  white-space: pre-wrap;
`;

// Styles for the WorklistMessage reminder

const WorklistMessageReminderWrapper = styled.div`
  border-left: 2px solid var(--primary-oqio-blue);
  color: var(--greyscale-blackish);
  display: flex;
  flex-direction: column;
  font-size: var(--font-size-small);
  line-height: var(--font-line-height-small);
  margin-bottom: 10px;
  margin-left: -15px;
  padding-left: 15px;
`;

const WorklistMessageReminderDate = styled.div`
  color: var(--primary-oqio-blue);
  display: flex;
  flex-direction: column;
  font-weight: var(--font-weight-semi-bold);
`;

const WorklistMessageReminderInfo = styled.div`
  display: flex;
  flex-direction: column;
  word-break: normal;
`;

// Styles for the WorklistMessage attachments

const WorklistMessageAttachmentsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 20px;
`;

const WorklistMessageFile = styled.div`
  display: flex;
  height: 20px;
  justify-content: space-between;
  width: 100%;
`;

const WorklistMessageAttachmentName = styled.div`
  align-items: center;
  color: var(--greyscale-shade);
  display: flex;
  flex-direction: row;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-regular);
  line-height: var(--font-line-height-small);

  &:hover {
    color: var(--primary-oqio-blue);

    svg {
      path {
        stroke: var(--primary-oqio-blue);
      }
    }
  }

  @media ${windowMaxWidth.mobile} {
    display: block !important;
    max-width: 26ch;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const WorklistMessageAttachmentButtonWrapper = styled.div`
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'auto' : 'pointer')};

  svg {
    path,
    circle {
      stroke: ${({ $isDisabled }) => ($isDisabled ? 'var(--greyscale-concrete)' : 'var(--primary-oqio-blue)')};
    }
  }

  &:hover {
    svg {
      path,
      circle {
        stroke: ${({ $isDisabled }) => ($isDisabled ? 'var(--greyscale-concrete)' : 'var(--primary-oqio-blue-hover)')};
      }
    }
  }
`;

// Styles for the WorklistMessage avatar wrapper
const WorklistMessageAvatarWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
`;

// Wrapper that stacks the avatar and action buttons in a column
const WorklistMessageSideColumn = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 0 5px;
`;

// Styles for Worklist Action Banners

const WorklistActionBannersListWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const WorklistActionBannerWrapper = styled.div`
  background-color: ${(props) => (props.$active ? 'var(--primary-oqio-blue)' : 'var(--greyscale-white)')};
  border-bottom: 1px solid var(--greyscale-dark-white);
  display: flex;
  gap: 10px;
  justify-content: space-between;
  padding: 20px 30px;
`;

const WorklistActionBannerTitle = styled.div`
  color: ${(props) => (props.$active ? 'var(--greyscale-white)' : 'var(--greyscale-blackish)')};
  font-size: var(--font-size-medium);
  font-weight: var(--font-weight-semi-bold);
  line-height: var(--font-line-height-regular);
`;

const WorklistActionBannerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  justify-content: space-between;
`;

const WorklistActionBannerDescription = styled.div`
  color: ${(props) => (props.$active ? 'var(--greyscale-white)' : 'var(--greyscale-shade)')};
`;

const WorklistActionBannerDescriptionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 30px;
  white-space: break-spaces;
`;

const WorklistActionBannerDescriptionList = styled.ul`
  margin: 0;
  padding-left: 15px;
`;

const WorklistActionBannerDescriptionListItem = styled.li`
  color: ${(props) => (props.$active ? 'var(--greyscale-white)' : 'var(--greyscale-shade)')};
  list-style: initial;
`;

const WorklistActionBannerActionButtonsWrapper = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

const WorklistActionBannerActionButtonsWithCheckboxWrapper = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: flex-end;
`;

const WorklistMessagesListLoadingWrapper = styled.div`
  align-items: center;
  background-color: var(--greyscale-white);
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
`;

export const WorklistHistoryChangeExpandableText = styled.p`
  font-size: var(--font-size-small);
  line-height: var(--font-line-height-small);
  margin: 0;
  overflow: hidden;
  position: relative;
  transition: max-height 0.2s;

  &.collapsed {
    /* stylelint-disable-next-line property-no-deprecated */
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${MAX_LINES};
    display: -webkit-box;
    max-height: ${MAX_LINES * LINE_HEIGHT_MESSAGE_HISTORY}px;
    text-overflow: ellipsis;
    white-space: pre-line;
  }

  &.expanded {
    /* stylelint-disable-next-line property-no-deprecated */
    -webkit-box-orient: unset;
    -webkit-line-clamp: unset;
    display: block;
    max-height: none;
    overflow: visible;
  }
`;

const WorklistMessageShowMoreActionWrapper = styled.div`
  margin-bottom: ${({ $hasMarginBottom }) => ($hasMarginBottom ? '5px' : '0')};

  button {
    font-size: var(--font-size-medium);
  }
`;

export const WorklistMessagesListLayout = {
  WorklistMessagesWrapper,
  WorklistMessagesListWrapper,
  WorklistMessageWrapper,
  WorklistUserMessageWrapper,
  WorklistMessageContent,
  WorklistMessageHeader,
  WorklistMessageSubHeader,
  WorklistMessageTitle,
  WorklistMessageRecipients,
  WorklistMessageCreatorWrapper,
  WorklistMessageCreatorUser,
  WorklistMessageCreatorSystem,
  WorklistMessageHeaderTitle,
  WorklistMessageHeaderDate,
  WorklistMessageDescription,
  WorklistMessageSystemDescription,
  WorklistMessageDescriptionList,
  WorklistMessageDescriptionListItem,
  WorklistMessageReminderWrapper,
  WorklistMessageReminderDate,
  WorklistMessageReminderInfo,
  WorklistMessageAttachmentsWrapper,
  WorklistMessageFile,
  WorklistMessageAttachmentName,
  WorklistMessageAttachmentButtonWrapper,
  WorklistMessageAvatarWrapper,
  WorklistMessageSideColumn,
  WorklistActionBannersListWrapper,
  WorklistActionBannerWrapper,
  WorklistActionBannerTitle,
  WorklistActionBannerContent,
  WorklistActionBannerDescriptionSection,
  WorklistActionBannerDescription,
  WorklistActionBannerDescriptionList,
  WorklistActionBannerDescriptionListItem,
  WorklistActionBannerActionButtonsWrapper,
  WorklistActionBannerActionButtonsWithCheckboxWrapper,
  WorklistMessagesListLoadingWrapper,
  WorklistMessageDividerWrapper,
  WorklistMessageDividerSentinel,
  WorklistHistoryChangeExpandableText,
  WorklistMessageShowMoreActionWrapper
};
